import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'

import { logGivingAction } from '@percent/workplace-giving/api/account/givingActions/logGivingAction/logGivingAction'
import type { GivingActionRow } from '@percent/workplace-giving/common/components/GivingActionCard/GivingActionCard.types'
import { GIVING_ACTION_LOG_EVENTS_QUERY_KEY } from '@percent/workplace-giving/common/components/LoggedDaysCalendar/useGivingActionLogEvents'
import { useLogger, useMutation } from '@percent/workplace-giving/common/hooks'

export type UseGivingActionLogging = Readonly<{
  selectedRow: GivingActionRow | null
  selectedDate: Date
  loggingRowId: string | null
  completedRowIds: Set<string>
  isLoggingAction: boolean
  setSelectedDate: (date: Date) => void
  handleViewAction: (row: GivingActionRow) => void
  handleLogAction: (row: GivingActionRow) => void
  handleConfirmCompletion: (row: GivingActionRow, date: Date) => void
  handleCloseModal: () => void
}>

export type UseGivingActionLoggingOptions = Readonly<{
  onLogCreated?: (rowId: string) => void
}>

export const useGivingActionLogging = ({
  onLogCreated
}: UseGivingActionLoggingOptions = {}): UseGivingActionLogging => {
  const queryClient = useQueryClient()
  const { logError } = useLogger()
  const [selectedRow, setSelectedRow] = useState<GivingActionRow | null>(null)
  const [selectedDate, setSelectedDate] = useState(() => new Date())
  const [loggingRowId, setLoggingRowId] = useState<string | null>(null)
  const [completedRowIds, setCompletedRowIds] = useState<Set<string>>(() => new Set())

  const { mutate: logAction, isLoading: isLoggingAction } = useMutation({
    mutationKey: ['logGivingAction'],
    mutationFn: logGivingAction,
    onSuccess: (response, variables) => {
      if (variables) {
        setCompletedRowIds(rowIds => new Set(rowIds).add(variables.rowId))
      }

      setSelectedRow(null)
      setLoggingRowId(null)

      // Re-logging a period the API already has comes back as `existing`.
      if (response?.status === 'created' && variables) {
        onLogCreated?.(variables.rowId)
      }

      queryClient.invalidateQueries({ queryKey: ['getAccountGivingActions'] })
      queryClient.invalidateQueries({ queryKey: [GIVING_ACTION_LOG_EVENTS_QUERY_KEY] })
    },
    onError: error => {
      logError(error)
      setLoggingRowId(null)
    }
  })

  const handleViewAction = (row: GivingActionRow) => {
    setSelectedDate(new Date())
    setSelectedRow(row)
  }

  const handleLogAction = (row: GivingActionRow) => {
    setSelectedDate(new Date())

    if (row.frequency === 'daily') {
      setSelectedRow(row)

      return
    }

    setLoggingRowId(row.id)
    logAction({ actionId: row.actionId, targetType: row.targetType, targetId: row.targetId, rowId: row.id })
  }

  const handleConfirmCompletion = (row: GivingActionRow, date: Date) => {
    setLoggingRowId(row.id)
    logAction({
      actionId: row.actionId,
      targetType: row.targetType,
      targetId: row.targetId,
      ...(row.frequency === 'daily' ? { completedAt: dayjs(date).format('YYYY-MM-DD') } : {}),
      rowId: row.id
    })
  }

  const handleCloseModal = () => {
    if (!isLoggingAction) {
      setSelectedRow(null)
    }
  }

  return {
    selectedRow,
    selectedDate,
    loggingRowId,
    completedRowIds,
    isLoggingAction,
    setSelectedDate,
    handleViewAction,
    handleLogAction,
    handleConfirmCompletion,
    handleCloseModal
  }
}
